import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Select, TextField, Typography } from '@glplus/glpweb-dsl';
import { runnerApi, RunnerApiError, type MyApplication } from '../lib/runnerApiClient';
import { TRANSPORT_OPTIONS } from '../lib/onboarding';
import { Panel } from './Panel';

const ORANGE = '#F67A1A';

/**
 * Applicant self-service (spec §5 stage 6, earliest form). Only the logistics of
 * how they want to work — phone, zones, transport mode. Name, email and date of
 * birth are deliberately absent: they are the identity the Checkr candidate is
 * built from (§5a), so corrections go through support where a human sees them.
 * The API enforces the same rule; this UI just doesn't offer what it would refuse.
 */
export function EditApplicationDetails({
  application,
  onSaved,
  onSuccess,
}: {
  application: MyApplication;
  onSaved: () => Promise<void>;
  /** Reported to the parent, because a success message rendered inside this
   *  panel would be unmounted by the very close that follows it. */
  onSuccess: (message: string) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState(application.phoneE164 ?? '');
  const [zones, setZones] = useState(application.zonesInterested.join(', '));
  const [transport, setTransport] = useState(application.transportMode);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState<{ kind: 'success' | 'error' | 'info'; msg: string } | null>(null);

  const reset = () => {
    setPhone(application.phoneE164 ?? '');
    setZones(application.zonesInterested.join(', '));
    setTransport(application.transportMode);
    setFlash(null);
  };

  const phoneOk = phone.trim() === '' || /^\+[1-9]\d{6,14}$/.test(phone.trim());

  const save = async () => {
    setBusy(true);
    setFlash(null);
    try {
      const res = await runnerApi.updateMyApplication({
        phoneE164: phone.trim() === '' ? null : phone.trim(),
        zonesInterested: zones
          .split(',')
          .map((z) => z.trim())
          .filter(Boolean),
        ...(transport !== application.transportMode ? { transportMode: transport } : {}),
      });
      if (res.changed) {
        await onSaved();
        setOpen(false);
        onSuccess(t('details.saved'));
      } else {
        setFlash({ kind: 'info', msg: t('details.nothing') });
      }
    } catch (err) {
      setFlash({
        kind: 'error',
        msg: err instanceof RunnerApiError ? err.message : t('details.saveFailed'),
      });
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <Button
        ariaLabel={t('details.edit')}
        buttonText={t('details.edit')}
        variant="outlined"
        size="small"
        sx={{ borderColor: '#d0d5dd', color: '#1f2937', fontWeight: 700 }}
        onClick={() => {
          reset();
          setOpen(true);
        }}
      />
    );
  }

  return (
    <Panel title={t('details.title')} accent={ORANGE} sx={{ mt: 2 }}>
      <Typography sx={{ color: '#4b5563', fontSize: '14px', mb: 2 }}>
        {t('details.intro')}
      </Typography>
      {flash && (
        <Box sx={{ mb: 2 }}>
          <Alert severity={flash.kind}>{flash.msg}</Alert>
        </Box>
      )}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
        <TextField
          label={t('details.phone')}
          ariaLabel={t('details.phone')}
          size="small"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          disabled={busy}
          placeholder="+17045550123"
        />
        <Select
          ariaLabel={t('details.transport')}
          label={t('details.transport')}
          native
          size="small"
          value={transport}
          onChange={(_e, v) => setTransport(String(v))}
          options={[
            ...(TRANSPORT_OPTIONS.some((m) => m.value === transport) ? [] : [transport]),
            ...TRANSPORT_OPTIONS.map((m) => m.value),
          ].map((v) => ({ value: v, label: t(`transport.${v}`) }))}
        />
      </Box>
      <Box sx={{ mt: 2 }}>
        <TextField
          label={t('details.zones')}
          ariaLabel={t('details.zones')}
          size="small"
          fullWidth
          value={zones}
          onChange={(e) => setZones(e.target.value)}
          disabled={busy}
          placeholder="28078, 28031"
        />
        <Typography sx={{ color: '#5f6b7a', fontSize: '12px', mt: 0.5 }}>
          {t('details.zonesHint')}
        </Typography>
      </Box>
      {!phoneOk && (
        <Typography sx={{ color: '#b42318', fontSize: '12px', mt: 1 }}>
          {t('details.phoneHint')}
        </Typography>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
        <Button
          ariaLabel={t('details.cancel')}
          buttonText={t('details.cancel')}
          variant="outlined"
          size="small"
          sx={{ borderColor: '#d0d5dd', color: '#1f2937' }}
          disabled={busy}
          onClick={() => {
            reset();
            setOpen(false);
          }}
        />
        <Button
          ariaLabel={t('details.saveAria')}
          buttonText={busy ? t('details.saving') : t('details.save')}
          variant="contained"
          size="small"
          sx={{ backgroundColor: ORANGE, '&:hover': { backgroundColor: '#d9670f' } }}
          disabled={busy || !phoneOk}
          onClick={() => void save()}
        />
      </Box>
    </Panel>
  );
}
